import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Package, Headphones, Book, FileText, CheckCircle, Loader2, Mic, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import VoiceRecorder from './VoiceRecorder';

const BundleSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [voiceId, setVoiceId] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [voiceData, setVoiceData] = useState(null);

  useEffect(() => {
    if (orderId) processOrder();
  }, [orderId]);

  const processOrder = async () => {
    setProcessingPayment(true);
    try {
      // Process the bundle order (Lulu submission etc)
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundle/process/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      setOrder(data);

      // Check if voice was stored in sessionStorage
      const storedVoice = sessionStorage.getItem(`bundle_voice_${orderId}`);
      if (storedVoice) {
        setVoiceData(storedVoice);
        sessionStorage.removeItem(`bundle_voice_${orderId}`);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Could not process order. Please contact support.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setProcessingPayment(false);
    }
  };

  const handleVoiceUpload = async (base64Data) => {
    setVoiceUploading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audio/upload-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio_data: base64Data,
          email: order?.contact_email || '',
          name: order?.shipping_name || 'User',
          autobiography_id: order?.autobiography_id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Voice upload failed');
      setVoiceId(data.voice_id);
      toast({ title: 'Voice Cloned!', description: 'Your voice has been successfully cloned. Generating audiobook...' });
      generateAudio(data.voice_id);
    } catch (err) {
      toast({ title: 'Voice Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setVoiceUploading(false);
    }
  };

  const handleStoredVoiceUpload = async () => {
    if (voiceData) {
      await handleVoiceUpload(voiceData);
    }
  };

  const generateAudio = async (vid) => {
    setAudioGenerating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundle/generate-audio/${orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice_id: vid }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Audio generation failed');
      setAudioReady(true);
      toast({ title: 'Audiobook Ready!', description: 'Your audiobook has been generated and is ready for download.' });
    } catch (err) {
      toast({ title: 'Audio Generation Failed', description: err.message, variant: 'destructive' });
    } finally {
      setAudioGenerating(false);
    }
  };

  const handleDownloadAudio = async () => {
    try {
      const autobiographyId = order?.autobiography_id;
      const email = order?.contact_email || '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audio/download/${autobiographyId}?email=${encodeURIComponent(email)}`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'autobiography_audiobook.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download Failed', description: err.message, variant: 'destructive' });
    }
  };

  if (loading || processingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin mx-auto" />
          <p className="text-gray-600 text-lg">Processing your bundle order...</p>
          <p className="text-sm text-gray-400">This may take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-12 px-4" data-testid="bundle-success-page">
      <div className="max-w-2xl mx-auto">
        {/* Success Banner */}
        <div className="glass-effect rounded-2xl p-8 mb-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full mb-4 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="bundle-success-title">Bundle Order Confirmed!</h1>
          <p className="text-gray-600 mb-1">Order #{orderId?.substring(0, 8)}</p>
          <p className="text-sm text-gray-500">Your hardcover book is being prepared for printing.</p>
        </div>

        {/* Status Cards */}
        <div className="space-y-4 mb-8">
          <div className="bg-white rounded-xl p-5 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Book className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">Hardcover Book</p>
              <p className="text-sm text-gray-500">Submitted to printer. Ships in 7-10 business days.</p>
            </div>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">Digital PDF</p>
              <p className="text-sm text-gray-500">Already available in your dashboard.</p>
            </div>
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>

          <div className={`bg-white rounded-xl p-5 shadow-sm ${!audioReady ? 'ring-2 ring-purple-300' : ''}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${audioReady ? 'bg-green-100' : 'bg-purple-100'}`}>
                <Headphones className={`w-6 h-6 ${audioReady ? 'text-green-600' : 'text-purple-600'}`} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">AI Audiobook</p>
                <p className="text-sm text-gray-500">
                  {audioReady ? 'Ready for download!' :
                    audioGenerating ? 'Generating your audiobook... this may take a few minutes.' :
                    voiceId ? 'Voice cloned! Starting audio generation...' :
                    voiceUploading ? 'Cloning your voice...' :
                    'Upload your voice sample to generate the audiobook.'}
                </p>
              </div>
              {audioReady && <CheckCircle className="w-5 h-5 text-green-500" />}
              {audioGenerating && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
            </div>

            {/* Voice Upload Section (if audio not yet generated) */}
            {!audioReady && !audioGenerating && !voiceId && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                {voiceData ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <p className="text-sm text-green-700">Voice sample from checkout is ready</p>
                    </div>
                    <Button
                      onClick={handleStoredVoiceUpload}
                      disabled={voiceUploading}
                      className="w-full btn-primary text-white py-4 rounded-full border-0"
                      data-testid="generate-audiobook-btn"
                    >
                      {voiceUploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cloning Voice...</>
                      ) : (
                        <><Headphones className="w-4 h-4 mr-2" /> Generate My Audiobook</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <VoiceRecorder
                    onVoiceReady={(base64) => handleVoiceUpload(base64)}
                    disabled={voiceUploading}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Download Button */}
        {audioReady && (
          <div className="mb-8">
            <Button
              onClick={handleDownloadAudio}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white py-6 text-lg rounded-xl border-0 flex items-center justify-center gap-2"
              data-testid="download-audiobook-btn"
            >
              <Headphones className="w-5 h-5" />
              Download Audiobook (MP3)
            </Button>
          </div>
        )}

        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="rounded-full px-8"
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BundleSuccess;
